/**
 * Settings — PRD 5.7, the reason this app got rebuilt.
 *
 * Everything on this screen is config that lives in D1, not in code: budget
 * categories (name, cap, color, column order) and family members (name, role,
 * sign-in email, color). Adding a category or letting a kid in is a form
 * submission, not a deploy.
 *
 * Adding the next panel — chore rates, grocery aisles, the rulebook — is a
 * <ConfigTable> plus a form, and it goes in the TABS array below.
 */
import { useEffect, useRef, useState } from 'react';
import { CategoryPanel } from './CategoryPanel';
import { FamilyPanel } from './FamilyPanel';
import { PlacesPanel } from './PlacesPanel';
import VerseSettingsPanel from '../verse/VerseSettingsPanel';
import WeatherLocationPanel from '../weather/WeatherLocationPanel';

type TabId = 'categories' | 'family' | 'places' | 'verse' | 'weather';

const TABS: { id: TabId; label: string }[] = [
  { id: 'categories', label: 'Categories' },
  { id: 'family', label: 'Family' },
  { id: 'places', label: 'Places' },
  { id: 'verse', label: 'Verse' },
  { id: 'weather', label: 'Weather' },
];

export default function AdminPage() {
  const [tab, setTab] = useState<TabId>('categories');
  const [status, setStatus] = useState('');
  const timer = useRef<number | null>(null);

  /** One polite live region for the whole screen: every save says what it did. */
  function announce(message: string) {
    setStatus(message);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setStatus(''), 5000);
  }

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-2xl font-semibold text-ink">Settings</h1>
        <p className="max-w-prose text-sm text-ink-soft">
          How the house runs. Caps, categories, colors and who's in the family all live here —
          change them and the app changes. Nothing here needs a developer.
        </p>
      </header>

      <div
        role="tablist"
        aria-label="Settings sections"
        className="flex gap-1 overflow-x-auto rounded-full border border-border bg-surface p-1"
      >
        {TABS.map((t) => {
          const selected = t.id === tab;
          return (
            <button
              key={t.id}
              role="tab"
              id={`tab-${t.id}`}
              type="button"
              aria-selected={selected}
              aria-controls={`panel-${t.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => setTab(t.id)}
              onKeyDown={(e) => {
                if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
                e.preventDefault();
                const index = TABS.findIndex((x) => x.id === tab);
                const next = (index + (e.key === 'ArrowRight' ? 1 : -1) + TABS.length) % TABS.length;
                setTab(TABS[next].id);
                document.getElementById(`tab-${TABS[next].id}`)?.focus();
              }}
              className={`min-h-11 flex-1 rounded-full px-4 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none ${
                selected
                  ? 'bg-sage-soft text-sage'
                  : 'text-ink-soft hover:bg-surface-sunk hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <p aria-live="polite" className={status ? 'text-sm font-medium text-sage' : 'sr-only'}>
        {status && <span aria-hidden="true">✓ </span>}
        {status}
      </p>

      <div
        role="tabpanel"
        id={`panel-${tab}`}
        aria-labelledby={`tab-${tab}`}
        tabIndex={-1}
        className="focus-visible:outline-none"
      >
        {tab === 'categories' ? (
          <CategoryPanel onStatus={announce} />
        ) : tab === 'family' ? (
          <FamilyPanel onStatus={announce} />
        ) : tab === 'places' ? (
          <PlacesPanel onStatus={announce} />
        ) : tab === 'verse' ? (
          <VerseSettingsPanel />
        ) : (
          <WeatherLocationPanel />
        )}
      </div>
    </div>
  );
}
